/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { useEffect, useRef, useState } from "react";
import {
	motion,
	useAnimation,
	useInView,
	useReducedMotion,
} from "motion/react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGsapScrollParallax } from "@/hooks/useGsapScrollParallax";

export function CtaSection() {
	const { t } = useTranslation();
	const sectionRef = useRef<HTMLDivElement>(null);
	const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
	const controls = useAnimation();
	const shouldReduceMotion = useReducedMotion();
	const isInView = useInView(sectionRef, { once: true, amount: 0.3 });
	useGsapScrollParallax(sectionRef, { y: -80, opacity: 0.92, scale: 0.985 });

	useEffect(() => {
		if (isInView) {
			controls.start("visible");
		}
	}, [isInView, controls]);

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: shouldReduceMotion ? 0 : 0.12,
				delayChildren: 0.1,
			},
		},
	};

	const itemVariants = {
		hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 16 },
		visible: {
			opacity: 1,
			y: 0,
			transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
		},
	};

	const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
		if (shouldReduceMotion) {
			return;
		}
		const rect = e.currentTarget.getBoundingClientRect();
		setMousePosition({
			x: ((e.clientX - rect.left) / rect.width) * 100,
			y: ((e.clientY - rect.top) / rect.height) * 100,
		});
	};

	return (
		<motion.section
			ref={sectionRef}
			initial="hidden"
			animate={controls}
			variants={containerVariants}
			className="site-section bg-background mt-10	">
			<div className="site-container">
				<motion.div
					variants={containerVariants}
					className="premium-card relative overflow-visible" // Changed from overflow-hidden to overflow-visible
					onMouseMove={handleMouseMove}>
					<motion.div
						className="pointer-events-none absolute inset-0 opacity-15 transition-opacity  duration-300"
						style={{
							background: `radial-gradient(560px circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(45,47,146,0.22), transparent 42%)`,
						}}
					/>

					<div className="relative z-10 px-4 py-8 sm:px-6 sm:py-10 md:px-8 md:py-12 lg:px-12 lg:py-16">
						<motion.div
							variants={containerVariants}
							className="flex flex-col items-start justify-between lg:flex-row lg:items-center ">
							{/* Below `lg` this used to be `hidden`, which is why the CTA
							    read as text-only on phones and tablets. It now renders at
							    every width; `object-contain` inside a ratio-stable box keeps
							    the artwork undistorted and reserves its space up front, so
							    showing it costs no layout shift. */}
							<motion.div
								variants={itemVariants}
								className="relative flex h-52 w-full max-w-115 items-center justify-center self-center sm:h-72 md:h-100 lg:h-105 lg:self-auto z-999 mb-6 lg:mb-0">
								<motion.img
									className="max-lg:h-full w-full object-contain lg:h-120"
									src="/images/pngegg.png"
									alt={t("common.ui.industrialFacility")}
									width={352}
									height={486}
									loading="lazy"
									decoding="async"
									initial={{ scale: 1 }}
									transition={{ duration: 0.3 }}
									style={{
										transformOrigin: "center center",
									}}
								/>
							</motion.div>

							<motion.div
								variants={containerVariants}
								className="w-full lg:w-auto">
								<motion.span
									variants={itemVariants}
									className="section-kicker">
									<span className="section-kicker-line" />
									{t("cta.kicker")}
								</motion.span>

								<motion.h2
									variants={itemVariants}
									className="section-title text-2xl sm:text-3xl md:text-4xl lg:text-5xl mt-3 sm:mt-4">
									{t("cta.title")}
								</motion.h2>

								<motion.p
									variants={itemVariants}
									className="section-subtitle max-w-xl text-sm sm:text-base md:text-lg mt-3 sm:mt-4">
									{t("cta.subtitle")}
								</motion.p>

								<motion.div
									variants={itemVariants}
									className="mt-5 sm:mt-6 md:mt-7">
									<Link
										to="/contact-us"
										className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-foreground text-background rounded-lg font-semibold hover:bg-opacity-90 transition-all duration-300 active:scale-95 w-full sm:w-auto">
										{t("cta.button")}
										<svg
											className="h-4 w-4"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24">
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M14 5l7 7m0 0l-7 7m7-7H3"
											/>
										</svg>
									</Link>
								</motion.div>
							</motion.div>
						</motion.div>
					</div>
				</motion.div>
			</div>
		</motion.section>
	);
}
